import { ExternalLink } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  COMPANY_RATING_LABELS,
  COMPANY_REGISTRY_STATUS_LABELS,
  INVOICE_OUTCOME_LABELS,
  PAYMENT_GRACE_DAYS,
  RATING_THRESHOLDS,
  SIGNAL_AREA_LABELS,
  SIGNAL_AREAS,
  SIGNAL_LEVEL_LABELS,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatInteger,
  formatPercent,
  formatReportValue,
  type AnnualFigures,
  type CompanyCheck,
  type CompanyKeyFigures,
  type CompanyRating,
  type InvoiceOutcome,
  type SignalLevel,
} from '@codepass/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Modal,
  Table,
  Td,
  Th,
  Tr,
  type BadgeTone,
} from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

/**
 * The company check on /kunder: the rating as Status, and the dialog that shows what it was
 * built from. Everything here is display - the rating is computed by `evaluateCompany` in
 * @codepass/shared, and the dialog only lays out what came back.
 */

const RATING_TONE: Record<CompanyRating, BadgeTone> = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
  critical: 'danger',
  unknown: 'neutral',
};

const LEVEL_TONE: Record<SignalLevel, BadgeTone> = {
  good: 'success',
  ok: 'neutral',
  weak: 'warning',
  bad: 'danger',
};

const OUTCOME_TONE: Record<InvoiceOutcome, BadgeTone> = {
  paid: 'success',
  credited: 'neutral',
  open: 'info',
  overdue: 'danger',
  unresolved: 'neutral',
};

export function CompanyRatingBadge({ check }: { check: Pick<CompanyCheck, 'rating' | 'score'> }) {
  return (
    <Badge tone={RATING_TONE[check.rating]}>
      {COMPANY_RATING_LABELS[check.rating]}
      {check.score !== null && check.rating !== 'critical' ? (
        <span className="tabular opacity-70">· {check.score}</span>
      ) : null}
    </Badge>
  );
}

/**
 * The badge as a button that opens the dialog.
 *
 * Sits inside a clickable table row, so the click must not reach the row - it would navigate
 * to the customer instead of opening the dialog.
 */
export function CompanyRatingButton({
  check,
  onOpen,
}: {
  check: CompanyCheck;
  onOpen: (check: CompanyCheck) => void;
}) {
  return (
    <button
      type="button"
      className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      title={`${check.summary}. Klik for at se grundlaget.`}
      aria-label={`Virksomhedstjek: ${COMPANY_RATING_LABELS[check.rating]}. Vis grundlaget.`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(check);
      }}
    >
      <CompanyRatingBadge check={check} />
    </button>
  );
}

function Section({
  title,
  children,
  aside,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="mt-6">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function SourceLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
    >
      {children}
      <ExternalLink className="h-3 w-3" aria-hidden />
    </a>
  );
}

function Facts({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 border-b border-line py-1">
          <dt className="text-ink-muted">{label}</dt>
          <dd className="text-right tabular">{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

type FigureRow =
  | { label: string; kind: 'money' | 'integer'; pick: (year: AnnualFigures) => number | null }
  | {
      label: string;
      kind: 'ratio';
      formula: string;
      pick: (figures: CompanyKeyFigures) => number | null;
    };

const FIGURE_ROWS: FigureRow[] = [
  { label: 'Omsætning', kind: 'money', pick: (year) => year.revenue },
  { label: 'Bruttofortjeneste', kind: 'money', pick: (year) => year.gross_profit },
  { label: 'Resultat af primær drift', kind: 'money', pick: (year) => year.operating_profit },
  { label: 'Resultat før skat', kind: 'money', pick: (year) => year.profit_before_tax },
  { label: 'Årets resultat', kind: 'money', pick: (year) => year.profit_loss },
  { label: 'Egenkapital', kind: 'money', pick: (year) => year.equity },
  { label: 'Balance', kind: 'money', pick: (year) => year.assets },
  { label: 'Omsætningsaktiver', kind: 'money', pick: (year) => year.current_assets },
  { label: 'Kortfristet gæld', kind: 'money', pick: (year) => year.short_term_liabilities },
  { label: 'Likvide beholdninger', kind: 'money', pick: (year) => year.cash },
  { label: 'Gns. antal ansatte', kind: 'integer', pick: (year) => year.employees },
  {
    label: 'Soliditetsgrad',
    kind: 'ratio',
    formula: 'egenkapital / balance',
    pick: (figures) => figures.equity_ratio,
  },
  {
    label: 'Likviditetsgrad',
    kind: 'ratio',
    formula: 'omsætningsaktiver / kortfristet gæld',
    pick: (figures) => figures.current_ratio,
  },
  {
    label: 'Afkastningsgrad',
    kind: 'ratio',
    formula: 'resultat af primær drift / balance',
    pick: (figures) => figures.return_on_assets,
  },
  {
    label: 'Egenkapitalforrentning',
    kind: 'ratio',
    formula: 'årets resultat / egenkapital',
    pick: (figures) => figures.return_on_equity,
  },
];

function AnnualReportSection({ check }: { check: CompanyCheck }) {
  const report = check.annual_report;
  if (!report) return null;

  const years = report.years;
  const value = (row: FigureRow, index: number): number | null => {
    if (row.kind === 'ratio') {
      const figures = check.key_figures[index];
      return figures ? row.pick(figures) : null;
    }
    const year = years[index];
    return year ? row.pick(year) : null;
  };
  const rows = FIGURE_ROWS.filter((row) => years.some((_, index) => value(row, index) !== null));

  const format = (row: FigureRow, number: number | null): string =>
    number === null
      ? '—'
      : row.kind === 'money'
        ? formatCurrency(number, report.currency, { decimals: 0 })
        : row.kind === 'integer'
          ? formatInteger(number)
          : formatPercent(number, 1);

  return (
    <Section
      title="Regnskab"
      aside={
        report.document_url ? (
          <SourceLink href={report.document_url}>Åbn årsrapporten</SourceLink>
        ) : null
      }
    >
      <p className="mb-2 text-xs text-ink-muted">
        Årsrapport for {formatDate(report.period_start)} – {formatDate(report.period_end)},
        offentliggjort {formatDate(report.published_at)}
        {report.reporting_class ? ` · ${report.reporting_class}` : ''}
        {report.auditor_assistance ? ` · ${report.auditor_assistance}` : ''}
        {report.going_concern ? ` · ${report.going_concern}` : ''}
        {report.auditor_opinion ? ` · ${report.auditor_opinion}` : ''}
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">
          Årsrapporten kunne ikke læses maskinelt (fx et IFRS-regnskab), så der er ingen tal.
        </p>
      ) : (
        <Table className="text-xs">
          <thead>
            <tr>
              <Th>Regnskabstal</Th>
              {years.map((year) => (
                <Th key={year.period_end} className="text-right">
                  {formatDate(year.period_end)}
                </Th>
              ))}
              <Th>Beregning</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <Tr key={row.label}>
                <Td className={cn(row.kind === 'ratio' && 'font-medium')}>{row.label}</Td>
                {years.map((year, index) => (
                  <Td key={year.period_end} className="text-right tabular">
                    {format(row, value(row, index))}
                  </Td>
                ))}
                <Td className="text-ink-muted">
                  {row.kind === 'ratio' ? row.formula : 'årsrapporten'}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
}

function PaymentsSection({ check }: { check: CompanyCheck }) {
  const payments = check.payments;
  if (!payments) return null;

  const money = (value: number) => formatCurrency(value, payments.currency, { decimals: 2 });

  return (
    <Section title="Betalinger i Business Central">
      {payments.invoice_count === 0 ? (
        <p className="text-sm text-ink-muted">Ingen bogførte fakturaer på kunden endnu.</p>
      ) : (
        <>
          <Facts
            items={[
              ['Betalingsfrist', `Netto ${payments.terms_days} dage`],
              ['Bogførte fakturaer', formatInteger(payments.invoice_count)],
              [
                'Betalt',
                `${formatInteger(payments.settled_count)} (heraf ${formatInteger(payments.on_time_count)} til tiden)`,
              ],
              [
                'Gns. betalingstid',
                payments.avg_days_late === null
                  ? '—'
                  : formatReportValue('days', Math.round(payments.avg_days_late)) +
                    ' efter forfald',
              ],
              ['Åbent', money(payments.open_amount)],
              ['Forfaldent', money(payments.overdue_amount)],
              [
                'Ældste forfald',
                payments.oldest_overdue_days === null
                  ? '—'
                  : formatReportValue('days', payments.oldest_overdue_days),
              ],
            ]}
          />
          <Table className="mt-3 text-xs">
            <thead>
              <tr>
                <Th>Faktura</Th>
                <Th>Bogført</Th>
                <Th>Forfald</Th>
                <Th className="text-right">Beløb</Th>
                <Th className="text-right">Rest</Th>
                <Th>{payments.exact ? 'Betalt' : 'Betalt (est.)'}</Th>
                <Th className="text-right">Dage efter forfald</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {payments.invoices.map((invoice) => (
                <Tr key={`${invoice.document_no}-${invoice.posting_date}`}>
                  <Td className="tabular">{invoice.document_no}</Td>
                  <Td className="tabular">{formatDate(invoice.posting_date)}</Td>
                  <Td className="tabular">{formatDate(invoice.due_date)}</Td>
                  <Td className="text-right tabular">{money(invoice.amount)}</Td>
                  <Td className="text-right tabular">{money(invoice.remaining)}</Td>
                  <Td className="tabular">{formatDate(invoice.settled_date)}</Td>
                  <Td className="text-right tabular">
                    {invoice.days_late ?? invoice.days_overdue ?? '—'}
                  </Td>
                  <Td>
                    <Badge tone={OUTCOME_TONE[invoice.outcome]}>
                      {INVOICE_OUTCOME_LABELS[invoice.outcome]}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Section>
  );
}

/**
 * The CVR lookup on /rapporter/virksomhedstjek: the same check and dialog as Status on /kunder,
 * for a company that is not a customer yet. The server validates the number.
 */
export function CompanyCvrLookup() {
  const [cvr, setCvr] = useState('');
  const [check, setCheck] = useState<CompanyCheck | null>(null);
  const lookup = useMutation({
    mutationFn: (value: string) =>
      api.get<CompanyCheck>('/reports/company-checks/cvr', { cvr: value }),
    onSuccess: setCheck,
  });

  return (
    <>
      <Card className="mb-5">
        <CardHeader>
          <CardTitle>Tjek en virksomhed</CardTitle>
          <p className="mt-1 text-xs text-ink-muted">
            Skriv et CVR-nummer for at vurdere en virksomhed, før der indgås en aftale. Vurderingen
            bygger på CVR-registret og årsrapporterne — en virksomhed, der ikke er kunde, har ingen
            betalinger i Business Central.
          </p>
        </CardHeader>
        <CardBody>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              lookup.mutate(cvr);
            }}
          >
            <Input
              value={cvr}
              onChange={(event) => setCvr(event.target.value)}
              inputMode="numeric"
              placeholder="12345678"
              aria-label="CVR-nummer"
              className="w-40"
            />
            <Button type="submit" disabled={lookup.isPending || cvr.trim() === ''}>
              {lookup.isPending ? 'Tjekker …' : 'Tjek'}
            </Button>
          </form>
          {lookup.error ? (
            <Alert tone="error" className="mt-3">
              {lookup.error.message}
            </Alert>
          ) : null}
        </CardBody>
      </Card>
      <CompanyCheckDialog check={check} onClose={() => setCheck(null)} />
    </>
  );
}

export function CompanyCheckDialog({
  check,
  onClose,
}: {
  check: CompanyCheck | null;
  onClose: () => void;
}) {
  if (!check) return null;

  const signals = [...check.signals].sort(
    (a, b) => SIGNAL_AREAS.indexOf(a.area) - SIGNAL_AREAS.indexOf(b.area),
  );
  const registry = check.registry;
  const currency = check.annual_report?.currency ?? check.payments?.currency ?? 'DKK';

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Virksomhedstjek · ${check.customer_name}`}
      description={check.cvr ? `CVR ${check.cvr}` : 'Intet dansk CVR-nummer'}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-base">
          <CompanyRatingBadge check={check} />
        </span>
        {check.score !== null ? (
          <span className="text-sm text-ink-muted">
            Score <span className="font-semibold text-ink tabular">{check.score}</span> af 100
          </span>
        ) : null}
      </div>
      <div className="mt-3 rounded-md border border-line bg-canvas px-3 py-2 text-sm">
        <p className="font-medium">{check.payment_expectation}</p>
        <p className="mt-0.5 text-xs text-ink-muted">{check.summary}</p>
      </div>

      <Section title="Det vurderingen bygger på">
        {signals.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Ingen data at vurdere ud fra — se kilderne nedenfor.
          </p>
        ) : (
          <Table className="text-xs">
            <thead>
              <tr>
                <Th>Område</Th>
                <Th>Signal</Th>
                <Th className="text-right">Værdi</Th>
                <Th>Vurdering</Th>
                <Th className="text-right">Vægt</Th>
                <Th>Forklaring</Th>
              </tr>
            </thead>
            <tbody>
              {signals.map((signal) => (
                <Tr key={signal.key}>
                  <Td className="whitespace-nowrap text-ink-muted">
                    {SIGNAL_AREA_LABELS[signal.area]}
                  </Td>
                  <Td className="whitespace-nowrap font-medium">{signal.label}</Td>
                  <Td className="whitespace-nowrap text-right tabular">
                    {formatReportValue(signal.type, signal.value, currency)}
                  </Td>
                  <Td>
                    <Badge tone={LEVEL_TONE[signal.level]}>
                      {SIGNAL_LEVEL_LABELS[signal.level]}
                    </Badge>
                  </Td>
                  <Td className="text-right tabular">{signal.weight}</Td>
                  <Td className="min-w-[220px] text-ink-muted">{signal.explanation}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {registry ? (
        <Section
          title="CVR-registret"
          aside={
            check.cvr ? (
              <SourceLink href={`https://datacvr.virk.dk/enhed/virksomhed/${check.cvr}`}>
                Åbn på Virk
              </SourceLink>
            ) : null
          }
        >
          <Facts
            items={[
              ['Navn', registry.name],
              ['Status', registry.status_text ?? COMPANY_REGISTRY_STATUS_LABELS[registry.status]],
              ['Virksomhedsform', registry.company_form],
              ['Branche', registry.industry],
              ['Stiftet', registry.founded_date ? formatDate(registry.founded_date) : null],
              ['Ophørt', registry.ended_date ? formatDate(registry.ended_date) : null],
              ['Ansatte', registry.employees],
            ]}
          />
        </Section>
      ) : null}

      <AnnualReportSection check={check} />
      <PaymentsSection check={check} />

      <Section title="Kilder">
        <ul className="space-y-1.5 text-xs">
          {check.sources.map((source) => (
            <li key={source.key} className="flex flex-wrap items-baseline gap-x-2">
              <Badge tone={source.ok ? 'success' : 'warning'}>
                {source.ok ? 'Hentet' : 'Mangler'}
              </Badge>
              <span className="font-medium">{source.label}</span>
              {source.fetched_at ? (
                <span className="text-ink-muted">{formatDateTime(source.fetched_at)}</span>
              ) : null}
              {source.url ? <SourceLink href={source.url}>Åbn</SourceLink> : null}
              {source.note ? (
                <span className="basis-full text-ink-muted">{source.note}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </Section>

      <details className="mt-6 text-xs text-ink-muted">
        <summary className="cursor-pointer font-medium text-ink">Sådan regnes vurderingen</summary>
        <div className="mt-2 space-y-1.5">
          <p>
            Hvert signal vurderes som god (100 point), acceptabel (70), svag (35) eller dårlig (0).
            Scoren er gennemsnittet vægtet efter signalernes vægt. Lav risiko fra{' '}
            {RATING_THRESHOLDS.low}, middel risiko fra {RATING_THRESHOLDS.medium}, ellers høj
            risiko.
          </p>
          <p>
            Et alvorligt fund trumfer gennemsnittet: konkurs eller ophør giver Kritisk, og tvivl om
            fortsat drift, et manglende regnskab eller fakturaer mere end 45 dage over forfald giver
            mindst Høj risiko. Ethvert dårligt signal udelukker Lav risiko.
          </p>
          <p>
            Regnskabstallene er fra virksomhedens seneste årsrapport hos Erhvervsstyrelsen, og
            nøgletallene er beregnet af Codepass. Betalingstiden er Business Centrals egne
            udligninger: hvornår og med hvilken betaling hver faktura blev lukket. En faktura lukket
            af en kreditnota tæller som krediteret, ikke betalt. Kan udligningerne ikke læses, er
            betalingstiden estimeret ved at fordele betalingerne på de ældste fakturaer først. En
            betaling højst {PAYMENT_GRACE_DAYS} dage efter forfald tæller som til tiden.
          </p>
        </div>
      </details>
    </Modal>
  );
}
